/**
 * Builds a windowed array of page numbers + '...' gap markers.
 *
 * Rules:
 *  - Always includes page 1 and page `total`.
 *  - Shows 1 sibling on each side of `current`.
 *  - Collapses any remaining gap into a single '...' marker.
 *  - If total ≤ 7 every page is shown with no ellipsis.
 *
 * Examples (total = 20):
 *   current =  1  →  [1, 2, '...', 20]
 *   current =  5  →  [1, '...', 4, 5, 6, '...', 20]
 *   current = 20  →  [1, '...', 19, 20]
 *
 * @param current  Current page, 1-based
 * @param total    Total number of pages
 */
export function buildPageWindows(
  current: number,
  total: number,
): (number | "...")[] {
  if (total <= 0) return [];
  if (total === 1) return [1];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | "...")[] = [];
  let prevWasEllipsis = false;

  for (let p = 1; p <= total; p++) {
    const show =
      p === 1 ||
      p === total ||
      (p >= current - 1 && p <= current + 1);

    if (show) {
      pages.push(p);
      prevWasEllipsis = false;
    } else if (!prevWasEllipsis) {
      pages.push("...");
      prevWasEllipsis = true;
    }
  }

  return pages;
}
